import React, { useCallback, useEffect, useState } from 'react';
/**
 * MultipleChoiceRenderer — Option selection with keyboard shortcuts.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s66.
 */
import { motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { QuestionRendererProps } from './types';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { shuffleOptions } from '../../../utils/deterministicShuffle';
/** Convert hex color to rgba string */
function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
const FALLBACK_COLOR = '#6366F1'; // indigo-500
export function MultipleChoiceRenderer({
  question,
  onAnswer,
  onReadyChange,
  moduleColor
}: QuestionRendererProps) {
  const color = moduleColor || FALLBACK_COLOR;
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [shuffledOptions, setShuffledOptions] = useState<Array<{
    text: string;
    originalIndex: number;
  }>>([]);
  useEffect(() => {
    setShuffledOptions(shuffleOptions(question.options, question.id));
    setSelectedOption(null);
  }, [question.id, question.options]);
  useEffect(() => {
    onReadyChange?.(selectedOption !== null);
  }, [selectedOption, onReadyChange]);
  const handleSelect = useCallback((index: number) => {
    setSelectedOption(index);
    if (shuffledOptions[index]) {
      onAnswer(shuffledOptions[index].originalIndex);
    }
  }, [shuffledOptions, onAnswer]);
  // Keyboard: 1-9 keys for quick selection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = parseInt(e.key);
      if (!isNaN(key) && key >= 1 && key <= 9) {
        const index = key - 1;
        if (index < shuffledOptions.length) {
          handleSelect(index);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shuffledOptions, handleSelect]);
  const isSelected = (idx: number) => selectedOption === idx;
  return <div className="grid grid-cols-1 gap-4" data-id="element-7563">
      {shuffledOptions.map((option, idx) => <motion.button key={option.originalIndex} initial={{
      opacity: 0,
      x: -20
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: 0.3 + idx * 0.1
    }} whileHover={{
      scale: 1.01,
      x: 4
    }} whileTap={{
      scale: 0.99
    }} onClick={() => handleSelect(idx)} className={`
            p-5 rounded-2xl text-left text-lg font-medium transition-all duration-200 border-2 min-h-[72px] flex items-center group relative
            ${isSelected(idx) ? 'shadow-md ring-2 ring-offset-2' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:shadow-sm'}
          `} style={isSelected(idx) ? {
      borderColor: color,
      backgroundColor: hexToRgba(color, 0.08),
      color: color,
      boxShadow: `0 4px 14px ${hexToRgba(color, 0.15)}`,
      // ring via outline to match Tailwind ring pattern
      outlineColor: hexToRgba(color, 0.3),
      // @ts-ignore -- ring-color as CSS var
      '--tw-ring-color': hexToRgba(color, 0.3)
    } : {
      // Hover border hint uses CSS variable
      '--hover-border': hexToRgba(color, 0.4)
    } as React.CSSProperties} onMouseEnter={e => {
      if (!isSelected(idx)) {
        e.currentTarget.style.borderColor = hexToRgba(color, 0.35);
      }
    }} onMouseLeave={e => {
      if (!isSelected(idx)) {
        e.currentTarget.style.borderColor = '';
      }
    }} data-id="element-7564">
          <motion.span animate={{
        scale: isSelected(idx) ? [1, 1.3, 1] : 1
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className={`
              inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold mr-4 transition-colors
              ${!isSelected(idx) ? 'bg-slate-100 text-slate-500' : ''}
            `} style={isSelected(idx) ? {
        backgroundColor: color,
        color: '#ffffff'
      } : undefined} onMouseEnter={e => {
        if (!isSelected(idx)) {
          ;
          (e.currentTarget as HTMLElement).style.backgroundColor = hexToRgba(color, 0.15);
          (e.currentTarget as HTMLElement).style.color = color;
        }
      }} onMouseLeave={e => {
        if (!isSelected(idx)) {
          ;
          (e.currentTarget as HTMLElement).style.backgroundColor = '';
          (e.currentTarget as HTMLElement).style.color = '';
        }
      }} data-id="element-7565">
            {isSelected(idx) ? <CheckIcon className="w-4 h-4" strokeWidth={3} data-id="element-7566" /> : String.fromCharCode(65 + idx)}
          </motion.span>
          <span className="flex-1" data-id="element-7567">
            <LatexRenderer content={option.text} data-id="element-7568" />
          </span>
          {idx < 9 && <span className="absolute right-4 top-1/2 -translate-y-1/2 bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-md text-xs font-bold text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity hidden md:flex items-center gap-1 shadow-sm" data-id="element-7569">
              <kbd className="font-mono" data-id="element-7570">{idx + 1}</kbd>
            </span>}
        </motion.button>)}
    </div>;
}